"use client";

import React, { useEffect, useMemo, useState } from "react";
import CustomSlider from '@/component/ui/CustomSlider/CustomSlider';
import { observeLozad } from "@/lib/utils/lozadObserver";
import ImageWrapper from "../ui/ImageWrapper";
import { useImagePageMedia } from "@/component/pdp/image-page/ImagePageMedia";
import { resolveModelImageUrl } from "@/component/pdp/image-page/galleryData";
import type { ImagePageGalleryData } from "@/component/pdp/image-page/types";

interface SliderWithThumbnailsProps {
	data: any;
	galleryData?: ImagePageGalleryData;
	priority?: boolean;
	isMobile?: boolean;
}

type HeroSlide = {
	src: string;
	galleryImageId?: string | number;
};

const MAX_VISIBLE_DOTS = 7;

const getVisibleDotIndices = (activeIndex: number, total: number) => {
	if (total <= MAX_VISIBLE_DOTS) {
		return Array.from({ length: total }, (_, index) => index);
	}

	let start = Math.max(0, activeIndex - Math.floor(MAX_VISIBLE_DOTS / 2));
	let end = start + MAX_VISIBLE_DOTS;

	if (end > total) {
		end = total;
		start = Math.max(0, end - MAX_VISIBLE_DOTS);
	}

	return Array.from({ length: end - start }, (_, index) => start + index);
};

const getDotSizeClass = (distance: number, isActive: boolean) => {
	if (isActive) return "h-2 w-6";
	if (distance === 1) return "h-2 w-2";
	if (distance === 2) return "h-1.5 w-1.5";
	return "h-1 w-1";
};

const resolveLegacySlide = (img: unknown): string => {
	if (typeof img === "string") {
		return resolveModelImageUrl(img);
	}
	if (img && typeof img === "object") {
		const record = img as Record<string, unknown>;
		return resolveModelImageUrl(record.url ?? record.src ?? record);
	}
	return resolveModelImageUrl("");
};

const SliderWithThumbnail: React.FC<SliderWithThumbnailsProps> = ({
	data,
	galleryData,
	priority = false,
	isMobile = false,
}) => {
	const [currentSlide, setCurrentSlide] = useState(0);
	const { openLightbox } = useImagePageMedia();

	useEffect(() => {
		observeLozad();
	}, [currentSlide]);

	const heroSlides: HeroSlide[] = useMemo(() => {
		if (galleryData?.images?.length) {
			return galleryData.images.map((image) => ({
				src: image.src,
				galleryImageId: image.id,
			}));
		}
		if (!Array.isArray(data?.images)) return [];
		return data.images.map((img: unknown, index: number) => ({
			src: resolveLegacySlide(img),
			galleryImageId: index,
		}));
	}, [data?.images, galleryData?.images]);

	const slideCount = heroSlides.length;
	const visibleDotIndices = useMemo(
		() => getVisibleDotIndices(currentSlide, slideCount),
		[currentSlide, slideCount],
	);

	useEffect(() => {
		setCurrentSlide((prev) => {
			if (slideCount <= 0) return 0;
			return Math.min(prev, slideCount - 1);
		});
	}, [slideCount]);

	const goToSlide = (index: number) => {
		if (slideCount <= 0) return;
		const nextIndex = Math.min(Math.max(0, index), slideCount - 1);
		setCurrentSlide(nextIndex);
	};
	const isDiscontinued = data?.status === 'discontinued';
	const imageWidth = isMobile ? 800 : 678;
	const imageHeight = isMobile ? 600 : 375;

	const openSlide = (index: number) => {
		const slide = heroSlides[index];
		if (!slide) return;
		openLightbox(slide.galleryImageId, "all", "all");
	};

	return (
		<div className="relative bg-white">
			<div className="relative product-slider h-auto w-full overflow-hidden rounded-lg border border-gray-200">
				{isDiscontinued && (
					<span className="absolute top-2 left-2 z-10 px-2 py-1 ">
							<ImageWrapper src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}discontinued-icon.svg`} alt="" width={82} height={82}/>
					</span>
				)}
				
				{slideCount > 0 ? (
					<CustomSlider
						responsive={{ mobile: 1, tablet: 1, desktop: 1 }}
						gap={0}
						showArrows={true}
						activeIndex={currentSlide}
						variant="boxed"
						className="w-full"
						onSlideChange={(index) => goToSlide(index)}
						itemsToShow={1}
						showDots={false}
						infinite={false}
					>
						{heroSlides.map((slide, index) => (
							<button
								key={`hero-slide-${index}-${String(slide.galleryImageId ?? "img")}`}
								type="button"
								onClick={() => openSlide(index)}
								aria-label={`${data.brand_name} ${data.name} Image ${index + 1}`}
								className="relative aspect-[4/3] w-full cursor-pointer overflow-hidden md:aspect-[678/375]"
							>
								<ImageWrapper
									src={slide.src}
									alt={`${data.brand_name} ${data.name} Image ${index + 1}`}
									className="rounded-lg object-cover"
									width={imageWidth}
									height={imageHeight}
									sourceWidth={imageWidth}
									sourceHeight={imageHeight}
									fill
									isMobile={isMobile}
									priority={priority && index === 0}
									unoptimized={false}
									sizes="(max-width:768px)100vw,50vw"
									title={`${data.brand_name} ${data.name} Image ${index + 1}`}
								/>
							</button>
						))}
					</CustomSlider>
				) : (
					<div className="relative aspect-[4/3] w-full md:aspect-[678/375]">
						<ImageWrapper
							src={`${process.env.NEXT_PUBLIC_CDN_URL}placeholder-car.webp`}
							alt="slide-placeholder"
							className="rounded-lg object-cover"
							height={imageHeight}
							width={imageWidth}
							fill
							isMobile={isMobile}
							sizes="(max-width:768px)100vw,50vw"
							title={`${data.brand_name} ${data.name} Image Placeholder`}
						/>
					</div>
				)}
			</div>
			{slideCount > 1 && (
				<div className="mt-3 flex min-h-[12px] items-center justify-center">
					<div className="flex items-center justify-center gap-1.5">
						{visibleDotIndices.map((dotIndex) => {
							const isActive = dotIndex === currentSlide;
							const distance = Math.abs(dotIndex - currentSlide);

							return (
								<button
									key={`dot-${dotIndex}`}
									type="button"
									onClick={(event) => {
										event.stopPropagation();
										goToSlide(dotIndex);
									}}
									aria-label={`Go to slide ${dotIndex + 1}`}
									aria-current={isActive ? "true" : undefined}
									className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
								>
									<span
										className={`block rounded-full transition-all duration-300 ease-in-out ${getDotSizeClass(distance, isActive)} ${
											isActive ? "bg-[#008C8B]" : "bg-[#D9D9D9]"
										}`}
									/>
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

export default SliderWithThumbnail;
